import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSupabaseAuth } from '@/hooks/useSupabaseAuth';
import { getSupabaseMode, supabase } from '@/lib/supabase';
import { API_URL } from '@/lib/socket';

const CONFIRM_WORD = 'BORRAR';

/**
 * Ajustes → «Borrar mi cuenta» (solo con cuenta real, no invitados). Pide escribir BORRAR; el
 * servidor comprueba la sesión y borra el perfil y el usuario (ver server/src/handlers/accountRoutes.ts).
 */
export const DeleteAccount = ({ onDone }: { onDone?: () => void }) => {
  const { isGuest, signOut } = useSupabaseAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [word, setWord] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (isGuest || getSupabaseMode() !== 'real') return null;

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) throw new Error('Tu sesión caducó. Vuelve a iniciar sesión e inténtalo de nuevo.');
      const res = await fetch(`${API_URL}/api/account/delete`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const body = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
      if (!res.ok || !body.ok) throw new Error(body.error ?? `Error ${res.status}`);
      await signOut();
      onDone?.();
      navigate('/', { replace: true, state: { notice: 'Tu cuenta y tus datos se borraron.' } });
    } catch (err) {
      setError((err as Error).message || 'No se pudo borrar la cuenta.');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="block text-xs font-semibold text-red-300 hover:underline" data-testid="delete-account">
        🗑️ Borrar mi cuenta
      </button>
    );
  }
  return (
    <div className="space-y-2 rounded-lg border border-red-500/40 bg-red-500/10 p-2 text-xs text-red-100" data-testid="delete-account-confirm">
      <p>
        Se borrarán tu cuenta, tu perfil y tus estadísticas (Elo, victorias…). <strong>No se puede deshacer.</strong>
      </p>
      <label className="block">
        Escribe <strong>{CONFIRM_WORD}</strong> para confirmar:
        <input
          value={word}
          onChange={(e) => setWord(e.target.value)}
          aria-label="Confirmación de borrado"
          className="mt-1 w-full rounded-md border border-white/10 bg-ink-900 px-2 py-1 text-slate-100"
        />
      </label>
      {error && <p role="alert">{error}</p>}
      <div className="flex gap-2">
        <button
          type="button"
          disabled={word.trim().toUpperCase() !== CONFIRM_WORD || busy}
          onClick={() => void remove()}
          className="flex-1 rounded-md bg-red-600 py-1.5 font-bold text-white disabled:opacity-40"
        >
          {busy ? 'Borrando…' : 'Borrar definitivamente'}
        </button>
        <button type="button" onClick={() => setOpen(false)} className="btn-ghost h-8 px-3 text-xs">
          Cancelar
        </button>
      </div>
    </div>
  );
};
